import React from "react";
import { ArrowRight, Bell, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../app/AppRootProviders";
import { Button } from "../../components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../../components/dropdown-menu";
import { formatActivity } from "../dashboard/utils/activityUtils";
import { useRecentActivityFeed } from "../recent-activity";

const formatDate = (dateString: string): string => {
  const differenceInHours = Math.floor((Date.now() - new Date(dateString).getTime()) / 3_600_000);
  if (differenceInHours < 1) return "Just now";
  if (differenceInHours < 24) return `${differenceInHours}h ago`;
  return `${Math.floor(differenceInHours / 24)}d ago`;
};

const RecentActivitiesDropdown: React.FC = () => {
  const { data: activities, isLoading, error, refetch } = useRecentActivityFeed();
  const navigate = useNavigate();
  const { showActivities, setShowActivities } = useAppContext();

  const handleViewAll = () => {
    setShowActivities(false);
    navigate("/recent-activity");
  };

  return (
    <DropdownMenu open={showActivities} onOpenChange={setShowActivities}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Recent Activities">
          <Bell className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" alignOffset={24} side="bottom" sideOffset={8} className="w-96 p-0">
        <div className="p-4 border-b font-semibold text-lg flex items-center justify-between">
          <span>Recent Activities</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refetch()}
            disabled={isLoading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y">
          {isLoading && !activities ? (
            <div className="p-4 text-center text-muted-foreground">Loading activities...</div>
          ) : null}
          {!isLoading && error ? (
            <div className="p-4 text-center text-muted-foreground">{error}</div>
          ) : null}
          {!isLoading && !error && activities?.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">No recent activities</div>
          ) : null}
          {activities?.map((activity) => (
            <div key={activity.id} className="flex items-start gap-3 p-4 bg-white dark:bg-gray-900">
              <div className="flex-1">
                <div className="font-medium">{formatActivity(activity).text}</div>
                <div className="text-xs text-gray-400 mt-1">{formatDate(activity.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewAll}
            className="w-full flex items-center justify-center gap-2"
          >
            View All Activities
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RecentActivitiesDropdown;
